/** Support tickets (P-15 opener + A-12/S-02 shared inbox) + DPDP self-service (P-16/P-17). */

import { apiFetch, downloadFile, fetchBlobUrl, newIdempotencyKey, rawFormFetch } from "./client";

/** One file on a message; fetched by its index (the API never sends storage keys — SUP-03). */
export interface SupportAttachment {
  index: number;
  filename: string;
  content_type: string;
}

export interface SupportMessage {
  id: string;
  author_user_id: string;
  author_role: string;
  body: string;
  /** Display filenames, one per attachment (older API builds sent storage keys here). */
  attachments: string[];
  /** Absent from API builds before SUP-03. */
  attachment_files?: SupportAttachment[];
  created_at: string;
  read_by_recipient_at: string | null;
}

export interface SupportTicket {
  id: string;
  opener_user_id: string;
  opener_role: string;
  subject: string;
  category: string;
  status: string;
  context_entity_type: string | null;
  context_entity_id: string | null;
  claimed_by_user_id: string | null;
  opened_at: string;
  first_response_at: string | null;
  resolved_at: string | null;
  last_message_at: string;
  message_count: number;
}

export interface SupportTicketDetail extends SupportTicket {
  messages: SupportMessage[];
}

export interface PaginatedTickets {
  data: SupportTicket[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

// ----- Opener (any role, /me/support) -----

export function openTicket(input: {
  subject: string;
  body: string;
  category: string;
  files: File[];
  idempotencyKey?: string;
}): Promise<SupportTicketDetail> {
  const form = new FormData();
  form.set("subject", input.subject);
  form.set("body", input.body);
  form.set("category", input.category);
  input.files.forEach((f) => form.append("attachments", f));
  return rawFormFetch("/api/v1/me/support/tickets", form, {
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey(),
  });
}

export const listMyTickets = (page = 1): Promise<PaginatedTickets> =>
  apiFetch(`/api/v1/me/support/tickets?page=${page}`);

export const getMyTicket = (id: string): Promise<SupportTicketDetail> =>
  apiFetch(`/api/v1/me/support/tickets/${id}`);

export function replyMyTicket(
  id: string,
  body: string,
  files: File[],
  idempotencyKey?: string,
): Promise<SupportTicketDetail> {
  const form = new FormData();
  form.set("body", body);
  files.forEach((f) => form.append("attachments", f));
  return rawFormFetch(`/api/v1/me/support/tickets/${id}/reply`, form, {
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export const reopenMyTicket = (id: string): Promise<SupportTicketDetail> =>
  apiFetch(`/api/v1/me/support/tickets/${id}/reopen`, { method: "POST" });

// ----- Shared inbox (admin + support, /support-inbox) -----

/** A page of the shared inbox; `perPage` (up to 100) defaults to the API's 25. */
export const inboxQueue = (tab = "unclaimed", page = 1, perPage?: number): Promise<PaginatedTickets> =>
  apiFetch(`/api/v1/support-inbox/tickets?tab=${tab}&page=${page}${perPage ? `&per_page=${perPage}` : ""}`);

export const inboxGet = (id: string): Promise<SupportTicketDetail> =>
  apiFetch(`/api/v1/support-inbox/tickets/${id}`);

export const inboxClaim = (id: string): Promise<SupportTicket> =>
  apiFetch(`/api/v1/support-inbox/tickets/${id}/claim`, { method: "POST" });

export function inboxReply(
  id: string,
  body: string,
  files: File[],
  idempotencyKey?: string,
): Promise<SupportTicketDetail> {
  const form = new FormData();
  form.set("body", body);
  files.forEach((f) => form.append("attachments", f));
  return rawFormFetch(`/api/v1/support-inbox/tickets/${id}/reply`, form, {
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/** Filename shown for an attachment entry: last path segment, minus the `<ts>-` prefix. */
export function attachmentName(key: string): string {
  const base = key.split("/").pop() ?? key;
  return base.replace(/^\d{8}-\d{6}-\d+-/, "");
}

/** A message's files, from `attachment_files`, or derived from `attachments` on older API builds. */
export function messageFiles(m: SupportMessage): SupportAttachment[] {
  return (
    m.attachment_files ??
    m.attachments.map((entry, index) => ({ index, filename: attachmentName(entry), content_type: "" }))
  );
}

/** Fetch an inbox-side (admin/support) attachment as a blob for in-app preview. */
export const loadInboxAttachment = (ticketId: string, messageId: string, index: number) =>
  fetchBlobUrl(`/api/v1/support-inbox/tickets/${ticketId}/messages/${messageId}/attachments/${index}`);

/** Fetch an opener-side (/me/support) attachment as a blob for in-app preview. */
export const loadMyAttachment = (ticketId: string, messageId: string, index: number) =>
  fetchBlobUrl(`/api/v1/me/support/tickets/${ticketId}/messages/${messageId}/attachments/${index}`);

export const inboxResolve = (id: string): Promise<SupportTicket> =>
  apiFetch(`/api/v1/support-inbox/tickets/${id}/resolve`, { method: "POST" });

export const inboxReopen = (id: string): Promise<SupportTicket> =>
  apiFetch(`/api/v1/support-inbox/tickets/${id}/reopen`, { method: "POST" });

// ----- DPDP self-service (P-16 / P-17) -----

export interface ExportRequest {
  id: string;
  scope: string;
  status: string;
  signed_url: string | null;
  signed_url_expires_at: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface DeletionRequest {
  id: string;
  scope: string;
  status: string;
  reason: string;
  reject_reason: string | null;
  reviewed_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export const createExport = (scope = "full"): Promise<ExportRequest> =>
  apiFetch("/api/v1/me/dpdp/exports", { method: "POST", body: { scope } });

export const listExports = (): Promise<ExportRequest[]> => apiFetch("/api/v1/me/dpdp/exports");

export const downloadExport = (id: string): Promise<void> =>
  downloadFile(`/api/v1/me/dpdp/exports/${id}/download`, "my-data-export.zip");

export const createDeletion = (reason: string, scope = "account"): Promise<DeletionRequest> =>
  apiFetch("/api/v1/me/dpdp/deletions", { method: "POST", body: { reason, scope } });

export const listDeletions = (): Promise<DeletionRequest[]> => apiFetch("/api/v1/me/dpdp/deletions");
